import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Leaf } from "lucide-react";
import { useLang, useLocalePath } from "../contexts/LanguageContext";

export default function Footer() {
  const { t } = useLang();
  const localePath = useLocalePath();
  const links = [
    ["nav.home", "/"], ["nav.about", "/about"], ["nav.services", "/services"],
    ["nav.training", "/training"], ["nav.products", "/products"],
    ["nav.gallery", "/gallery"], ["nav.blog", "/blog"], ["nav.contact", "/contact"],
  ];

  return (
    <footer data-testid="site-footer" className="relative bg-forest text-ivory overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: "radial-gradient(rgba(255,255,255,0.5) 1px, transparent 1px)",
        backgroundSize: "28px 28px",
      }} />

      <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 pt-20 lg:pt-28 pb-10">
        <h2 className="font-serif font-light text-[14vw] lg:text-[9.5vw] leading-[0.9] tracking-tight text-ivory/95">
          {t("meta.site_name").split(" ").slice(0, 2).join(" ")}
          <span className="block italic text-mustard/90">{t("meta.site_name").split(" ").slice(2).join(" ") || "."}.</span>
        </h2>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-6">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-ivory/10 ring-1 ring-ivory/20">
                <img src="/images/logo.png" alt="" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="text-[12px] tracking-[0.3em] uppercase text-mustard/90">{t("footer.eyebrow")}</div>
                <div className="font-serif text-xl">{t("footer.line1")}</div>
              </div>
            </div>
            <p className="text-[15px] leading-relaxed text-ivory/75 max-w-md">{t("footer.about")}</p>
          </div>

          <div className="md:col-span-3">
            <div className="text-[11px] tracking-[0.3em] uppercase text-mustard/80 mb-5">{t("footer.explore")}</div>
            <ul className="space-y-3 text-[15px]">
              {links.map(([key, path]) => (
                <li key={path}>
                  <Link to={localePath(path)} className="link-sweep text-ivory/80 hover:text-mustard" data-testid={`footer-link-${key.replace('nav.','')}`}>
                    {t(key)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-[11px] tracking-[0.3em] uppercase text-mustard/80 mb-5">{t("footer.get_in_touch")}</div>
            <ul className="space-y-4 text-[15px] text-ivory/85">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-mustard shrink-0" />
                <span className="whitespace-pre-line">{t("contact_page.address")}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-mustard" />
                <a href="tel:+919938056120" className="link-sweep">+91 99380 56120</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-mustard" />
                <a href="mailto:lspnaturalfarming@gmail.com" className="link-sweep break-all">lspnaturalfarming@gmail.com</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-ivory/15 flex flex-col md:flex-row items-center justify-between gap-4 text-[12px] text-ivory/55">
          <div className="flex items-center gap-2">
            <Leaf className="w-3.5 h-3.5 text-mustard" />
            <span>© {new Date().getFullYear()} {t("meta.site_name")}. {t("footer.rights")}</span>
          </div>
          <div className="flex items-center gap-5">
            <span data-testid="footer-credit">
              Built & Managed by{" "}
              <a href="https://redpumpkin.in" target="_blank" rel="noopener noreferrer"
                className="text-mustard hover:text-ivory link-sweep"
                data-testid="footer-redpumpkin-link">
                Red Pumpkin Marketing
              </a>
            </span>
            <Link to="/admin/login" className="hover:text-mustard" data-testid="footer-admin-link">{t("footer.admin")}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
